/**
 * ChurchEventsTab — read-only list of the events tagged to this church or
 * province. Tags are set on the event itself (Event workspace → Churches &
 * Provinces), so nothing here is editable.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CalendarDays, ExternalLink } from 'lucide-react';
import { eventAPI } from '@/lib/api';

const RELATION_LABEL = {
  direct: 'Tagged to this unit',
  inherited: 'From province',
  below: 'Under this unit',
};

const fmt = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const venueName = (venue) => {
  if (!venue) return '';
  if (typeof venue === 'string') return venue;
  return venue.name || venue.city || '';
};

export const ChurchEventsTab = ({ churchId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['church-tagged-events', churchId],
    queryFn: () => eventAPI.listForChurch(churchId).then((r) => r.data),
    enabled: !!churchId,
  });

  if (isLoading) {
    return (
      <div className="py-20 text-center" data-testid="church-events-loading">
        <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
      </div>
    );
  }

  const events = Array.isArray(data) ? data : [];

  return (
    <Card data-testid="church-events-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base md:text-lg">
          <CalendarDays className="h-5 w-5 text-emerald-600" />
          Events
        </CardTitle>
        <CardDescription>
          Ministry events tagged to this unit — its own, anything inherited from its
          province, and anything happening beneath it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <p className="text-sm text-rose-600" data-testid="church-events-error">
            {error.message || 'Could not load events'}
          </p>
        )}
        {!error && !events.length && (
          <p className="text-sm text-slate-500 py-10 text-center" data-testid="church-events-empty">
            No events tagged to this unit yet.
          </p>
        )}
        <div className="space-y-2">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="rounded-xl border border-slate-200 p-3 flex items-start justify-between gap-3 hover:border-emerald-300 transition-colors"
              data-testid={`church-event-${ev.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{ev.name}</p>
                <p className="text-[11px] text-slate-500">
                  {[fmt(ev.start_date), venueName(ev.venue)].filter(Boolean).join(' · ')}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {!ev.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                  {(ev.tagged_via || []).map((t) => (
                    <Badge key={`${t.unit_id}-${t.relation}`} variant="secondary" className="text-[10px] font-normal">
                      {RELATION_LABEL[t.relation] || t.relation}
                      {t.relation !== 'direct' && t.unit_name ? `: ${t.unit_name}` : ''}
                    </Badge>
                  ))}
                </div>
              </div>
              {ev.slug && (
                <a
                  href={`/events/${ev.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-emerald-700 hover:underline shrink-0 inline-flex items-center gap-1"
                  data-testid={`church-event-open-${ev.id}`}
                >
                  View <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChurchEventsTab;
